import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { BrandMark, Wordmark } from "../common/Brand";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/resources", label: "Travel Resources" },
  { to: "/blog", label: "The Escape Edit" },
  { to: "/gallery", label: "Captured Escapes" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-md bg-white/95" : "bg-white"
      } border-b border-hairline`}
    >
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
          <BrandMark size={56} />
          <span className="hidden sm:block font-serif italic text-xl text-ink tracking-tight">
            Luxe<span className="not-italic">T</span>ini <span className="text-gold">Escapes</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `nav-link text-[13px] uppercase tracking-[0.22em] font-light transition-colors ${
                  isActive ? "text-gold active" : "text-ink/70 hover:text-ink"
                }`
              }
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/plan-your-escape"
            className="hidden md:inline-flex items-center justify-center h-11 px-5 text-[11px] uppercase tracking-[0.2em] whitespace-nowrap bg-gold text-ink hover:bg-goldDeep transition-colors"
            data-testid="header-plan-cta"
          >
            Plan Your Escape
          </Link>
          <button
            className="lg:hidden text-ink p-2"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} strokeWidth={1.25} /> : <Menu size={22} strokeWidth={1.25} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 bg-white border-t border-hairline ${
          open ? "max-h-[500px]" : "max-h-0"
        }`}
        data-testid="mobile-menu"
      >
        <div className="px-6 py-6 flex flex-col gap-5">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              className={({ isActive }) =>
                `text-[14px] uppercase tracking-[0.22em] ${isActive ? "text-gold" : "text-ink/70"}`
              }
              data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/plan-your-escape"
            className="mt-2 inline-flex items-center justify-center h-12 px-6 text-[12px] uppercase tracking-[0.26em] bg-gold text-ink"
            data-testid="mobile-plan-cta"
          >
            Plan Your Escape
          </Link>
        </div>
      </div>
    </header>
  );
}
